import { useSelector } from '@tanstack/react-form';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardTemplateDto } from '../../../api/cards';
import { withForm } from '../../../form-hook';
import { formProps } from '../../../utils/form-props';
import type { CardsSearchPatch } from '../cards-url-state';
import { FileDropZone } from '../FileDropZone';
import { selectedTplFor } from './card-batch';
import { cardGenFormOpts, type useCardTemplatePick } from './use-card-batch';

/**
 * Step 1 — the SVG. Either a saved template (whose front side is staged as a
 * File, so everything downstream sees one shape) or a one-off upload. Picking a
 * saved one clears the entered `form:` values with it: they belong to whichever
 * template asked for them.
 */
export const TemplateStep = withForm({
  ...cardGenFormOpts,
  props: formProps<{
    template: ReturnType<typeof useCardTemplatePick>;
    savedTemplates: CardTemplateDto[];
    onSearchPatch?: (patch: CardsSearchPatch) => void;
  }>(),
  render: function TemplateStep({
    form,
    template,
    savedTemplates,
    onSearchPatch,
  }) {
    const { t } = useTranslation();
    const templateSelectId = useId();
    const selectedTemplateId = useSelector(
      form.store,
      (state) => state.values.templateId,
    );
    const templateFile = useSelector(
      form.store,
      (state) => state.values.templateFile,
    );
    // Derived here from what this step already subscribes to, so the parent
    // does not have to subscribe to the whole form to hand them down.
    const selectedTpl = selectedTplFor(selectedTemplateId, savedTemplates);
    const allVarsCount = new Set([...template.frontVars, ...template.backVars])
      .size;
    return (
      <div>
        {savedTemplates.length > 0 && (
          <div className="mb-2">
            <label
              htmlFor={templateSelectId}
              className="mb-1 block text-xs font-medium text-ink"
            >
              {t('cardGenerator.templateLabel')}
            </label>
            <select
              id={templateSelectId}
              value={selectedTemplateId}
              onChange={(e) => {
                void template.selectSaved(e.target.value);
                // Changing template resets the form values — and the sort
                // field, which is one of the outgoing template's variables.
                onSearchPatch?.({
                  template: e.target.value || undefined,
                  values: undefined,
                  sortBy: undefined,
                });
              }}
              className="w-full px-3 py-2 text-sm"
            >
              <option value="">{t('cardGenerator.templatePlaceholder')}</option>
              {savedTemplates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                  {tpl.description ? ` — ${tpl.description}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {!selectedTemplateId && (
          <FileDropZone
            label={
              savedTemplates.length > 0
                ? t('cardGenerator.templateUpload')
                : t('cardGenerator.templateUploadOnly')
            }
            accept=".svg,image/svg+xml"
            hint={t('cardGenerator.templateHint')}
            file={templateFile}
            onFile={(f) => {
              void template.pickFile(f);
              // A custom upload can't be URL-shared; drop any stale id.
              onSearchPatch?.({
                template: undefined,
                values: undefined,
                sortBy: undefined,
              });
            }}
            summary={
              template.frontVars.length > 0
                ? t('cardGenerator.templateVars', {
                    count: template.frontVars.length,
                  })
                : undefined
            }
          />
        )}

        {selectedTemplateId && templateFile && (
          <div className="space-y-1 border border-blue bg-blue/4 px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue">
                {selectedTpl?.name}
              </span>
              <button
                type="button"
                onClick={() => {
                  template.clear();
                  onSearchPatch?.({
                    template: undefined,
                    values: undefined,
                    sortBy: undefined,
                  });
                }}
                className="text-xs text-blue"
              >
                {t('cardGenerator.clearTemplate')}
              </button>
            </div>
            {selectedTpl?.backSide && (
              <p className="text-xs text-magenta">
                {t('cardGenerator.templateVerso', {
                  name: selectedTpl.backSide.name,
                })}
              </p>
            )}
            {allVarsCount > 0 && (
              <p className="text-xs text-muted-soft">
                {t('cardGenerator.templateVars', { count: allVarsCount })}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
});
